import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../i18n/I18nContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import YouTubeEmbed from "../components/YouTubeEmbed";
import PaymentRegionsBlock from "../components/PaymentRegionsBlock";
import usePageMeta from "../lib/usePageMeta";
import { Calendar, Clock, ArrowRight, Check, Sparkles } from "lucide-react";

const VIDEO_URL = "https://youtu.be/I6jD490RAuk";

export default function CourseFomasyonWeb() {
  const { t } = useI18n();
  usePageMeta({
    title: t("coursePages.web.title") + " · Seayisyen",
    description: t("coursePages.web.subtitle"),
    path: "/fomasyon-web",
  });

  const highlights = t("coursePages.web.highlights");
  const safeHighlights = Array.isArray(highlights) ? highlights : [];

  return (
    <div data-testid="course-web-page">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#141414]" data-testid="course-web-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-black to-black pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="overline mb-4 text-[#d4af37]" data-testid="course-web-overline">
            {t("coursePages.web.overline")}
          </div>
          <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight" data-testid="course-web-title">
            {t("coursePages.web.title")}
          </h1>
          <p className="mt-5 max-w-2xl text-neutral-300 text-base md:text-lg leading-relaxed" data-testid="course-web-subtitle">
            {t("coursePages.web.subtitle")}
          </p>

          {/* Quick facts */}
          <div className="mt-8 flex flex-wrap gap-3" data-testid="course-web-facts">
            <Fact icon={<Calendar size={14} className="text-[#d4af37]" />} label={t("coursePages.web.startLabel")} value={t("coursePages.web.startValue")} />
            <Fact icon={<Clock size={14} className="text-[#d4af37]" />} label={t("coursePages.web.durationLabel")} value={t("coursePages.web.durationValue")} />
            <Fact icon={<Sparkles size={14} className="text-[#e6192b]" />} label={t("coursePages.web.priceLabel")} value="$499 / $399" red />
          </div>

          {/* CTA */}
          <div className="mt-10">
            <Link
              to="/enroll?tier=standard"
              data-testid="course-web-cta"
              className="btn-imperial inline-flex items-center gap-2"
            >
              {t("coursePages.web.cta")} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* VIDEO + HIGHLIGHTS */}
      <section className="border-b border-[#141414] py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-5 md:px-10 grid lg:grid-cols-5 gap-10 items-start">
          <div className="lg:col-span-3" data-testid="course-web-video-wrap">
            <div className="overline mb-3">{t("coursePages.web.videoOverline")}</div>
            <YouTubeEmbed
              url={VIDEO_URL}
              title="Fòmasyon Web — Seayisyen 2026"
              testId="course-web-video"
            />
          </div>
          <div className="lg:col-span-2" data-testid="course-web-highlights">
            <div className="overline mb-3">{t("coursePages.web.highlightsOverline")}</div>
            <h2 className="font-serif text-2xl md:text-3xl mb-6">{t("coursePages.web.highlightsTitle")}</h2>
            <ul className="space-y-3">
              {safeHighlights.map((h, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-neutral-300 leading-relaxed" data-testid={`course-web-highlight-${i + 1}`}>
                  <Check size={16} className="text-[#d4af37] mt-0.5 flex-shrink-0" /> {h}
                </li>
              ))}
            </ul>
            <Link
              to="/enroll?tier=standard"
              className="btn-outline-gold inline-flex items-center gap-2 mt-8"
              data-testid="course-web-cta-secondary"
            >
              {t("coursePages.web.cta")} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* PAYMENT */}
      <section className="py-16 md:py-20" data-testid="course-web-payment-section">
        <div className="max-w-5xl mx-auto px-5 md:px-10">
          <div className="mb-10">
            <div className="overline mb-3">{t("payInfo.overline")}</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight">{t("payInfo.title")}</h2>
            <p className="mt-3 text-neutral-400 max-w-2xl">{t("payInfo.subtitle")}</p>
          </div>
          <PaymentRegionsBlock idPrefix="course-web" />
        </div>
      </section>

      <Footer />
    </div>
  );
}

function Fact({ icon, label, value, red }) {
  return (
    <div className={`border px-4 py-3 flex items-center gap-2 ${red ? "border-[#e6192b]/40 bg-[#e6192b]/5" : "border-[#d4af37]/40 bg-[#d4af37]/5"}`}>
      {icon}
      <div className="flex flex-col leading-tight">
        <span className={`overline text-[10px] ${red ? "text-[#e6192b]" : "text-[#d4af37]"}`}>{label}</span>
        <span className="text-sm text-white font-condensed tracking-wide">{value}</span>
      </div>
    </div>
  );
}
